import { useState, useEffect, useCallback, useRef } from 'react';
import { getDealersBranches, getCompaniesAndSites } from '../service/telemetricDashboardService';

const useEntityPickerData = (selectedManufacturer = 'toyota') => {
  // All cached data
  const [dealerBranchData, setDealerBranchData] = useState([]);
  const [companySiteData, setCompanySiteData] = useState(null); // Full response from last API call
  const [initialCounts, setInitialCounts] = useState({ company: 0, site: 0 }); // Initial counts from API
  
  // Selected values
  const [selectedFilters, setSelectedFilters] = useState({
    dealer: null,
    branch: null,
    company: null,
    site: null
  });
  
  // Search state
  const [isSearching, setIsSearching] = useState(false); // True when waiting for search selection
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const hasLoadedInitialData = useRef(false);
  const lastLoadedDealerId = useRef(null); // Track the last dealer ID we loaded data for
  const lastLoadedManufacturer = useRef(null); // Track the last company we loaded data for

  // Load dealer-branch data and initial company-site counts on mount or when company changes
  useEffect(() => {
    // Reset if company changed
    if (lastLoadedManufacturer.current !== selectedManufacturer) {
      hasLoadedInitialData.current = false;
      lastLoadedDealerId.current = null;
      setDealerBranchData([]);
      setCompanySiteData(null);
      setSelectedFilters({
        dealer: null,
        branch: null,
        company: null,
        site: null
      });
    }
    
    if (hasLoadedInitialData.current && lastLoadedManufacturer.current === selectedManufacturer) return;
    
    const loadInitialData = async () => {
      try {
        setLoading(true);
        // Load both APIs in parallel
        const [dealerBranchResult, countsResult] = await Promise.all([
          getDealersBranches(selectedManufacturer),
          getCompaniesAndSites({ manufacturer: selectedManufacturer })
        ]);
        
        setDealerBranchData(dealerBranchResult);
        
        // API returns [{ CompanyCount: X, SiteCount: Y }] when no filters
        if (countsResult && countsResult.length > 0 && countsResult[0].CompanyCount !== undefined) {
          setInitialCounts({
            company: countsResult[0].CompanyCount || 0,
            site: countsResult[0].SiteCount || 0
          });
        }
        
        hasLoadedInitialData.current = true;
        lastLoadedManufacturer.current = selectedManufacturer;
      } catch (err) {
        console.error('Error loading initial data:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    
    loadInitialData();
  }, [selectedManufacturer]);

  // Fetch company-site data based on filters or search
  const fetchCompanySiteData = useCallback(async (params = {}) => {
    try {
      // Check if we're trying to load the same dealer data we already have
      if (params.dealerId && params.dealerId === lastLoadedDealerId.current && !params.searchCompany && !params.searchSite) {
        // Same dealer, no search terms - skip API call, use cached data
        console.log('Skipping API call - data already loaded for dealer:', params.dealerId);
        return companySiteData;
      }
      
      setLoading(true);
      const data = await getCompaniesAndSites({ ...params, manufacturer: selectedManufacturer });
      // API returns flat array of company-site relationships
      setCompanySiteData(data);
      
      // Track the dealer ID if we loaded dealer-specific data
      if (params.dealerId && !params.searchCompany && !params.searchSite) {
        lastLoadedDealerId.current = params.dealerId;
      }
      
      return data;
    } catch (err) {
      console.error('Error fetching company-site data:', err);
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [companySiteData, selectedManufacturer]);

  // Handle search for company or site
  const handleSearch = useCallback((level, term) => {
    
    // Check if we already have data loaded (dealer selected)
    const hasExistingData = companySiteData && Array.isArray(companySiteData) && companySiteData.length > 0;
    
    if (hasExistingData) {
      // Data exists: Do local search, no API call needed
      // Component will filter locally using the search term
      setIsSearching(false);
      return;
    }
    
    // No data exists: Trigger API search and enter search mode
    if (!term.trim()) {
      // Empty search term, clear search mode
      setIsSearching(false);
      return;
    }
    
    const params = {};
    if (level === 'company' && term) {
      params.searchCompany = term;
    } else if (level === 'site' && term) {
      params.searchSite = term;
    }
    
    // Enter search mode (disable other entities until selection)
    setIsSearching(true);
    
    fetchCompanySiteData(params);
  }, [companySiteData, fetchCompanySiteData]);

  // Handle filter selection
  const handleFilterChange = useCallback((level, value) => {
    setSelectedFilters(prev => {
      const newFilters = { ...prev, [level]: value };
      
      // Auto-select related entities based on what was selected
      if (level === 'dealer') {
        // Clear lower levels when dealer changes
        newFilters.branch = null;
        newFilters.company = null;
        newFilters.site = null;
        
        // Exit search mode
        setIsSearching(false);
        
        // Load company-site data for this dealer (loads ALL data for dealer)
        if (value) {
          const params = { dealerId: value };
          fetchCompanySiteData(params);
        } else {
          // Dealer cleared, reset company-site data
          setCompanySiteData(null);
        }
      } else if (level === 'branch') {
        // Clear lower levels when branch changes
        newFilters.company = null;
        newFilters.site = null;
        
        // Auto-select dealer if we can find it
        const branchData = dealerBranchData.find(d => d.BranchId === value);
        if (branchData) {
          newFilters.dealer = branchData.DealerId;
          
          // Only fetch data if dealer is not already selected
          // If dealer is selected, we already have all the data
          if (!prev.dealer) {
            const params = { dealerId: branchData.DealerId };
            fetchCompanySiteData(params);
          }
        }
        
        // If branch is cleared but dealer is selected, keep the dealer's data
        // No API call needed as we already have all data for the dealer
      } else if (level === 'company' && value && companySiteData) {
        // Auto-select dealer, branch from company data
        const companyRecord = companySiteData.find(record => record.CompanyId === value);
        if (companyRecord) {
          if (companyRecord.DealerId) {
            newFilters.dealer = companyRecord.DealerId;
            
            // Exit search mode and load all data for this dealer
            setIsSearching(false);
            
            // Load all company-site data for this dealer
            const params = { dealerId: companyRecord.DealerId };
            fetchCompanySiteData(params);
          }
          if (companyRecord.BranchId) newFilters.branch = companyRecord.BranchId;
        }
        newFilters.site = null;
      } else if (level === 'site' && value && companySiteData) {
        // Auto-select dealer, branch, company from site data
        const siteRecord = companySiteData.find(record => record.SiteId === value);
        if (siteRecord) {
          if (siteRecord.DealerId) {
            newFilters.dealer = siteRecord.DealerId;
            
            // Exit search mode and load all data for this dealer
            setIsSearching(false);
            
            // Load all company-site data for this dealer
            const params = { dealerId: siteRecord.DealerId };
            fetchCompanySiteData(params);
          }
          if (siteRecord.BranchId) newFilters.branch = siteRecord.BranchId;
          if (siteRecord.CompanyId) newFilters.company = siteRecord.CompanyId;
        }
      }
      
      return newFilters;
    });
  }, [dealerBranchData, companySiteData, fetchCompanySiteData]);

  // Get available dealers
  const getDealers = useCallback(() => {
    const dealerMap = new Map();
    dealerBranchData.forEach(item => {
      if (!dealerMap.has(item.DealerId)) {
        dealerMap.set(item.DealerId, {
          value: item.DealerId,
          label: item.DealerCode ? `${item.DealerCode} - ${item.DealerName}` : item.DealerName,
          name: item.DealerName,
          code: item.DealerCode
        });
      }
    });
    return Array.from(dealerMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [dealerBranchData]);

  // Get available branches (filtered by selected dealer if any)
  const getBranches = useCallback(() => {
    let filteredData = dealerBranchData;
    
    if (selectedFilters.dealer) {
      filteredData = dealerBranchData.filter(d => d.DealerId === selectedFilters.dealer);
    }
    
    const branchMap = new Map();
    filteredData.forEach(item => {
      if (!branchMap.has(item.BranchId)) {
        branchMap.set(item.BranchId, {
          value: item.BranchId,
          label: item.BranchCode ? `${item.BranchCode} - ${item.BranchName}` : item.BranchName,
          name: item.BranchName,
          code: item.BranchCode
        });
      }
    });
    return Array.from(branchMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [dealerBranchData, selectedFilters.dealer]);

  // Get available companies (from cached API response)
  const getCompanies = useCallback(() => {
    if (!companySiteData || !Array.isArray(companySiteData)) return [];
    
    // Extract unique companies from flat array
    const companyMap = new Map();
    companySiteData.forEach(record => {
      if (record.CompanyId && !companyMap.has(record.CompanyId)) {
        // Filter by selected branch if dealer is selected (data already loaded for dealer)
        if (selectedFilters.branch && record.BranchId !== selectedFilters.branch) {
          return;
        }
        
        companyMap.set(record.CompanyId, {
          value: record.CompanyId,
          label: record.CompanyCode ? `${record.CompanyCode} - ${record.CompanyName}` : record.CompanyName,
          name: record.CompanyName,
          code: record.CompanyCode,
          dealerId: record.DealerId,
          branchId: record.BranchId
        });
      }
    });
    
    return Array.from(companyMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [companySiteData, selectedFilters.branch]);

  // Get available sites (from cached API response, filtered by selected company if any)
  const getSites = useCallback(() => {
    if (!companySiteData || !Array.isArray(companySiteData)) return [];
    
    // Extract unique sites from flat array
    const siteMap = new Map();
    companySiteData.forEach(record => {
      if (record.SiteId && !siteMap.has(record.SiteId)) {
        // Filter by selected branch if dealer is selected (data already loaded for dealer)
        if (selectedFilters.branch && record.BranchId !== selectedFilters.branch) {
          return;
        }
        
        // If company is selected, only show sites for that company
        if (selectedFilters.company && record.CompanyId !== selectedFilters.company) {
          return;
        }
        
        siteMap.set(record.SiteId, {
          value: record.SiteId,
          label: record.SiteCode ? `${record.SiteCode} - ${record.SiteName}` : record.SiteName,
          name: record.SiteName,
          code: record.SiteCode,
          companyId: record.CompanyId,
          dealerId: record.DealerId,
          branchId: record.BranchId
        });
      }
    });
    
    return Array.from(siteMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [companySiteData, selectedFilters.branch, selectedFilters.company]);

  // Get total counts from company-site data or initial counts
  const getTotalCounts = useCallback(() => {
    const counts = {
      dealer: getDealers().length,
      branch: getBranches().length,
      company: 0,
      site: 0
    };
    
    // If we have actual company-site data, count from it
    if (companySiteData && Array.isArray(companySiteData) && companySiteData.length > 0 && companySiteData[0].CompanyId) {
      counts.company = getCompanies().length;
      counts.site = getSites().length;
    } else {
      // Otherwise use initial counts from the count-only API call
      counts.company = initialCounts.company;
      counts.site = initialCounts.site;
    }
    
    return counts;
  }, [getDealers, getBranches, getCompanies, getSites, companySiteData, initialCounts]);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setSelectedFilters({
      dealer: null,
      branch: null,
      company: null,
      site: null
    });
    setIsSearching(false);
    // Clear company-site data to show initial counts again
    setCompanySiteData(null);
    // Clear the last loaded dealer ID
    lastLoadedDealerId.current = null;
  }, []);

  // Cancel search mode (when user clicks clear/cross button)
  const cancelSearch = useCallback(() => {
    setIsSearching(false);
    // Keep companySiteData if dealer is selected, otherwise clear it
    if (!selectedFilters.dealer) {
      setCompanySiteData(null);
    }
  }, [selectedFilters.dealer]);

  return {
    selectedFilters,
    loading,
    error,
    isSearching,
    dealers: getDealers(),
    branches: getBranches(),
    companies: getCompanies(),
    sites: getSites(),
    handleFilterChange,
    handleSearch,
    cancelSearch,
    clearAllFilters,
    totalCounts: getTotalCounts()
  };
};

export default useEntityPickerData;
